import { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Archive, FileText, Receipt, Car, FileInput } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

function monthRange(year, month) {
    const y = Number(year);
    if (!month) return { start: `${y}-01-01`, end: `${y}-12-31` };
    const m = Number(month);
    const last = new Date(y, m, 0).getDate();
    return { start: `${y}-${String(m).padStart(2, "0")}-01`, end: `${y}-${String(m).padStart(2, "0")}-${last}` };
}

export function AccountantPackage({ year, month }) {
    const [range, setRange] = useState(() => monthRange(year, month));
    const [preview, setPreview] = useState(null);

    useEffect(() => { setRange(monthRange(year, month)); }, [year, month]);

    useEffect(() => {
        if (!range.start || !range.end || range.start > range.end) { setPreview(null); return; }
        api.get(`/accounting/package/preview?start=${range.start}&end=${range.end}`)
            .then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, [range]);

    const download = () => {
        if (range.start > range.end) { toast.error("Datum „od“ musí být před datem „do“"); return; }
        window.open(`${API_BASE}/accounting/package?start=${range.start}&end=${range.end}`, "_blank");
        toast.success("Balíček se připravuje — stahování začne za okamžik");
    };

    return (
        <div className="bg-white border border-slate-200 mb-8" data-testid="accountant-package">
            <div className="px-6 py-4 border-b-2 border-slate-900 flex items-center gap-3">
                <Archive className="w-5 h-5 text-orange-600" />
                <div>
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Podklady pro účetní (ZIP)</div>
                    <div className="text-xs text-slate-500">Vydané i přijaté faktury (PDF/soubory), účtenky k nákladům, kniha jízd a přehledy v CSV — vše za období jedním kliknutím.</div>
                </div>
            </div>
            <div className="p-6 flex flex-wrap items-end gap-4">
                <div>
                    <label className="label-eyebrow block mb-1.5">Od</label>
                    <input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="package-start"
                           className="px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600" />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Do</label>
                    <input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="package-end"
                           className="px-3 py-2 border border-slate-300 bg-white text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600" />
                </div>
                <div className="flex-1 flex flex-wrap gap-3 text-xs text-slate-600" data-testid="package-preview">
                    {preview ? (
                        <>
                            <Stat icon={FileText} label="faktur" value={preview.invoices} sub={fmt(preview.invoices_amount)} />
                            <Stat icon={FileInput} label="přijatých" value={preview.received} sub={fmt(preview.received_amount)} />
                            <Stat icon={Receipt} label="nákladů" value={preview.costs} sub={`${preview.receipts} s dokladem`} />
                            <Stat icon={Car} label="jízd" value={preview.trips} />
                        </>
                    ) : <span className="self-center text-slate-400">Vyberte platné období…</span>}
                </div>
                <button onClick={download} disabled={!preview} data-testid="package-download"
                        className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <Archive className="w-4 h-4" /> Stáhnout ZIP
                </button>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value, sub }) {
    return (
        <div className="flex items-center gap-2 border border-slate-200 px-3 py-2 bg-slate-50 rounded-sm">
            <Icon className="w-4 h-4 text-orange-600" />
            <div>
                <div className="font-bold text-slate-900 tabular-data">{value} <span className="font-normal text-slate-500">{label}</span></div>
                {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
            </div>
        </div>
    );
}
